import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useShareData } from "../contexts/ShareDataContext";
import { isCommentAuthor, isQuestionAuthor } from "../utils/ownershipUtils.js";
import RichTextEditor from "./RichTextEditor";
import { normalizeCommentHtml, isHtmlEmpty } from "../utils/sanitize";
import "./QuestionAccordion.css";

function QuestionAccordion({
  question,
  projectId,
  onCreateComment,
  onMarkAsAnswer,
  onDeleteComment,
  onDeleteQuestion,
  categories,
  isSelected,
  onToggleSelect,
  projectOwnerId,
}) {
  const { user } = useAuth();
  const { shareData } = useShareData();
  const [isOpen, setIsOpen] = useState(false);
  const [commentHtml, setCommentHtml] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submitComment = async () => {
    if (isHtmlEmpty(commentHtml) || submitting) return;

    setSubmitting(true);
    try {
      const safeHtml = normalizeCommentHtml(commentHtml);
      await onCreateComment(question.id, safeHtml);
      setCommentHtml("");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    await submitComment();
  };

  const handleMarkAsAnswer = async (commentId, currentStatus) => {
    try {
      await onMarkAsAnswer(question.id, commentId, !currentStatus);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to mark as answer");
    }
  };

  const sortedComments = [...(question.comments || [])].sort((a, b) => {
    if (a.is_answer && !b.is_answer) return -1;
    if (!a.is_answer && b.is_answer) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const category = categories?.find((c) => c.id === question.category_id);

  return (
    <div className={`question-accordion ${question._isNew ? "question-enter" : ""} ${question._isDeleting ? "question-exit" : ""}`} id={`question-${question.id}`}>
      <div className="question-header">
        {onToggleSelect && (
          <input
            type="checkbox"
            checked={isSelected || false}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelect(question.id);
            }}
            className="question-checkbox"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <div
          className="question-header-content"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="question-title-row">
            <div className="question-content">
              <h3 className="question-title">{question.content}</h3>
              <div className="question-author">
                <span className="author-name">
                  {isQuestionAuthor(question, user, shareData)
                    ? "You"
                    : question.creator?.name || "Unknown"}
                </span>
              </div>
            </div>
            <div className="question-meta">
              {category && (
                <span className="question-category">{category.name}</span>
              )}
              <span className={`question-status status-${question.status}`}>
                {question.status === "untouched" && "Unanswered"}
                {question.status === "in_progress" && "In Progress"}
                {question.status === "answered" && "Answered"}
              </span>
            </div>
          </div>
        </div>
        <div className="question-header-actions">
          <button
            className="question-delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteQuestion(question.id);
            }}
            disabled={!isQuestionAuthor(question, user, shareData)}
            id={`delete-question-${question.id}`}
            name={`delete-question-${question.id}`}
            title={isQuestionAuthor(question, user, shareData) ? "Delete question" : "You can only delete your own questions"}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </button>
          <button
            className="question-toggle"
            onClick={() => setIsOpen(!isOpen)}
            title={isOpen ? "Collapse" : "Expand"}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {isOpen ? (
                <line x1="5" y1="12" x2="19" y2="12"></line>
              ) : (
                <>
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="question-expanded-content">
          <div
            className="comment-form"
            id={`comment-form-${question.id}`}
          >
            <RichTextEditor
              id={`comment-editor-${question.id}`}
              value={commentHtml}
              onChange={setCommentHtml}
              onSubmitHotkey={submitComment}
              placeholder="Add a comment..."
            />
            <button
              type="button"
              className="btn btn-sm"
              disabled={submitting || isHtmlEmpty(commentHtml)}
              id={`submit-comment-${question.id}`}
              name={`submit-comment-${question.id}`}
              onClick={submitComment}
            >
              {submitting && <span className="spinner"></span>}
              {submitting ? "Adding..." : "Add Comment"}
            </button>
          </div>

          <div className="comments-list">
            {sortedComments.length === 0 ? (
              <p className="no-comments">No comments yet</p>
            ) : (
              sortedComments.map((comment) => (
                <div
                  key={comment.id}
                  className={`comment ${comment.is_answer ? "comment-answer" : ""} ${comment._isNew ? "comment-enter" : ""} ${comment._isDeleting ? "comment-exit" : ""}`}
                  id={`comment-${comment.id}`}
                >
                  {comment.is_answer && (
                    <div className="answer-badge">✓ Answer</div>
                  )}
                  <div className="comment-header">
                    <div className="comment-author">
                      <span className="author-name">
                        {isCommentAuthor(comment, user, shareData)
                          ? "You"
                          : comment.user?.name || "Unknown"}
                      </span>
                    </div>
                    <div className="comment-actions">
                      {typeof projectOwnerId !== 'undefined' && Number(comment.user_id) !== Number(projectOwnerId) && (
                        <button
                          className="btn-link"
                          onClick={() =>
                            handleMarkAsAnswer(comment.id, comment.is_answer)
                          }
                          id={`mark-answer-${comment.id}`}
                          name={`mark-answer-${comment.id}`}
                        >
                          {comment.is_answer
                            ? "Unmark as Answer"
                            : "Mark as Answer"}
                        </button>
                      )}
                      {isCommentAuthor(comment, user, shareData) && (
                        <button
                          className="btn-link delete-link"
                          onClick={() => onDeleteComment(question.id, comment.id)}
                          id={`delete-comment-${comment.id}`}
                          name={`delete-comment-${comment.id}`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  <div
                    className="comment-content"
                    dangerouslySetInnerHTML={{
                      __html: normalizeCommentHtml(comment.content || ""),
                    }}
                  />
                  <span className="comment-date">
                    {new Date(comment.created_at).toLocaleString()}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default QuestionAccordion;
